import { Grid } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AutocompleteDropdown } from 'src/shared/components/form/autocompleteDropdown';
import { useGlobals } from 'src/shared/contexts/appContext';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Association } from 'src/shared/models/association/association';
import { District } from 'src/shared/models/district/district';
import { SelectableItem } from 'src/shared/models/gui/selectableItem';
import { getAssociations } from 'src/shared/services/associationService';
import { getDistricts } from 'src/shared/services/districtService';

type UserDistrictAssociationDropdownProps = {
  preSelectedAssociation?: number;
  preSelectedDistrict?: number;
};

export const UserDistrictAssociationDropdown = (props: UserDistrictAssociationDropdownProps) => {
  const { preSelectedAssociation, preSelectedDistrict } = props;
  const { t } = useTranslation(['user', 'member']);
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();
  const { setValue, watch } = useFormContext();

  const [associationData, setAssociationData] = useState<Association[]>([]);
  const [districtData, setDistrictData] = useState<District[]>([]);
  const [loadingDistricts, setLoadingDistricts] = useState<boolean>(false);
  const [loadingAssociation, setLoadingAssociation] = useState<boolean>(false);

  const district = watch('districts');
  const association = watch('associations');

  const handleSelectDistrictItem = async (val: SelectableItem | null) => {
    if (val === null) {
      setValue('districts', []);
      return;
    }
    if (districtData) {
      const selectedDistrict = districtData.find((item) => val.value === item.countyAss_id.toString());
      if (selectedDistrict) {
        setValue('districts', [selectedDistrict]);
        return;
      }
    }
    setValue('districts', []);
  };

  const handleSelectAssociationItem = async (val: SelectableItem | null) => {
    if (val === null) {
      setValue('associations', []);
      return;
    }
    if (associationData) {
      const selectedAssociation = associationData.find((item) => val.value === item.localAss_id.toString());
      if (selectedAssociation) {
        setValue('associations', [selectedAssociation]);
        return;
      }
    }
    setValue('associations', []);
  };

  const fetchDistrict = useCallback(async () => {
    setLoadingDistricts(true);
    try {
      const result = await getDistricts(0, 100, 'countyAss', 'desc');
      if (isMounted()) {
        setDistrictData(result.data.results);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        setLoadingDistricts(false);
      }
    }
  }, [isMounted]);

  useEffect(() => {
    fetchDistrict();
  }, [fetchDistrict]);

  const fetchAssociation = useCallback(async () => {
    setLoadingAssociation(true);
    try {
      const result = await getAssociations();
      if (isMounted()) {
        setAssociationData(result.data.results);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) {
        setLoadingAssociation(false);
      }
    }
  }, [isMounted]);

  useEffect(() => {
    fetchAssociation();
  }, [fetchAssociation]);

  useEffect(() => {
    if (preSelectedAssociation !== undefined) {
      const preSelectedItem = associationData.find((x) => x.localAss_id === preSelectedAssociation);
      if (preSelectedItem) {
        setValue('associations', [preSelectedItem]);
      }
    }

    if (preSelectedDistrict !== undefined) {
      const preSelectedDistrictItem = districtData.find((x) => x.countyAss_id === preSelectedDistrict);
      if (preSelectedDistrictItem) {
        setValue('districts', [preSelectedDistrictItem]);
      }
    }
  }, [associationData, districtData, preSelectedAssociation, preSelectedDistrict, setValue]);

  return (
    <Grid container>
      {!!currentUser && currentUser?.districtCount > 0 && (
        <Grid item xs={12} mt={2}>
          <AutocompleteDropdown
            emptyLabel={t('user:selectDistrict')}
            placeholder={t('user:selectDistrict')}
            value={
              preSelectedDistrict && district.length > 0
                ? {
                    label: district[0].countyAss || '',
                    value: district[0].countyAss_id || '',
                  }
                : null
            }
            disable={association.length > 0 || !!preSelectedAssociation || !!preSelectedDistrict}
            loading={loadingDistricts}
            options={(districtData ?? []).map((x) => ({
              label: x.countyAss,
              value: x.countyAss_id.toString(),
            }))}
            nameProp='label'
            keyProp='value'
            name='districts'
            callback={(val) => handleSelectDistrictItem(val)}
            label={t('user:district')}
            componentWidth='100%'
          />
        </Grid>
      )}
      <Grid item xs={12} mt={2}>
        <AutocompleteDropdown<SelectableItem>
          emptyLabel={t('user:selectAssociation')}
          placeholder={t('user:selectAssociation')}
          value={
            preSelectedAssociation && association.length > 0
              ? {
                  label: association[0].localAss || '',
                  value: association[0].localAss_id || '',
                }
              : null
          }
          disable={district.length > 0 || !!preSelectedAssociation || !!preSelectedDistrict}
          loading={loadingAssociation}
          options={(associationData ?? []).map((x) => ({
            label: x.localAss,
            value: x.localAss_id.toString(),
          }))}
          nameProp='label'
          keyProp='value'
          name='associations'
          callback={(val) => handleSelectAssociationItem(val)}
          label={t('user:association')}
          componentWidth='100%'
        />
      </Grid>
    </Grid>
  );
};
